/**
 * Why a locked step or section is shut: the steps it waits on, as links, or
 * the read-only state that means they cannot be finished.
 *
 * @module router/programme-editor/LockedNotice
 */

import React from "react";

import { Link } from "react-router";

import { WarningAlert } from "@/components/ui";
import type { LockPrerequisite } from "@/state/step-locks";

import type { EditorAvailability } from "./editor-availability";
import { pathForStateKey } from "./surfaces";

export interface LockedNoticeProps {
  /** The programme's base address, which the prerequisite links resolve against. */
  base: string;
  /** The step this is about, named first where the page does not already. */
  subject?: string;
  /** What the step waits on. Empty when something other than setup shuts it. */
  prerequisites: readonly LockPrerequisite[];
  /** Shown as given when no prerequisite explains the lock. */
  reason: string;
  /** Present while the programme is read-only, which stops a prerequisite being finished. */
  readOnly: EditorAvailability | null;
  /** The reason is already on the page, so the notice does not repeat it. */
  reasonShown?: boolean;
  "data-testid"?: string;
}

/** The prerequisites as links, in prose: "A", "A and B", "A, B and C". */
function PrerequisiteLinks({
  base,
  prerequisites,
}: Pick<LockedNoticeProps, "base" | "prerequisites">): React.ReactElement {
  return (
    <>
      {prerequisites.map((step, index) => {
        const path = pathForStateKey(base, step.key);
        // The step's own anchor: a section can hold two, and Identity holds CAS.
        const to = path ? `${path}#step-${step.key}` : null;
        let separator = "";
        if (index > 0) {
          separator = index === prerequisites.length - 1 ? " and " : ", ";
        }
        return (
          <React.Fragment key={step.key}>
            {separator}
            {to ? (
              <Link
                to={to}
                className="font-semibold underline"
                data-testid={`locked-link-${step.key}`}
              >
                {step.label}
              </Link>
            ) : (
              step.label
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

export function LockedNotice({
  base,
  subject = undefined,
  prerequisites,
  reason,
  readOnly,
  reasonShown = false,
  "data-testid": testId,
}: LockedNoticeProps): React.ReactElement {
  const lead = subject ? `${subject}: ` : "";
  if (prerequisites.length === 0) {
    return (
      <WarningAlert data-testid={testId}>
        {lead}
        {reason}
      </WarningAlert>
    );
  }
  // Asking a reader to finish a step the programme will not let them edit is
  // a dead end, so the notice says the programme is read-only and why.
  if (readOnly) {
    return (
      <WarningAlert data-testid={testId}>
        {lead}
        <PrerequisiteLinks base={base} prerequisites={prerequisites} /> cannot be completed here.
        {reasonShown ? null : ` ${readOnly.full}`}
      </WarningAlert>
    );
  }
  return (
    <WarningAlert data-testid={testId}>
      {lead}Complete <PrerequisiteLinks base={base} prerequisites={prerequisites} /> first
    </WarningAlert>
  );
}
